import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Copy, Save, AlertTriangle, TrendingUp, TrendingDown, DollarSign, Share2, ChevronDown, RefreshCw, User, X } from 'lucide-react';
import { useAuth, getPortfolios, savePortfolio, deletePortfolios, logActivity, createShareToken, inviteClient, getLatestApproval, getSettings, getLinkedClient, unlinkClient, sendInviteEmail, getClientStatusForPortfolio } from '../context/AuthContext';
import AllocationPieChart from '../components/AllocationPieChart';
import { INSTRUMENTS, BENCHMARKS, BENCHMARK_META } from '../lib/mockData';
import { getRealPerformanceReturns, getRealRiskMetrics, clearMarketCaches } from '../lib/finnhub';
import TickerSearch from '../components/TickerSearch';
import PerformanceChart from '../components/PerformanceChart';
import HoldingsPerformanceChart from '../components/HoldingsPerformanceChart';
import StatusBadge, { getPortfolioStatus } from '../components/StatusBadge';
import ConfirmModal from '../components/ConfirmModal';
import MessagePanel from '../components/MessagePanel';
import TickerSummaryModal from '../components/TickerSummaryModal';
import SchwabLinkButton from '../components/SchwabLinkButton';
import { isConfigured as isSchwabConfigured } from '../lib/schwab';
import { useToast } from '../context/ToastContext';
import { useMarketData } from '../context/MarketDataContext';

const TIMEFRAMES = [
  { label: '1D', days: 1 },
  { label: '7D', days: 5 },
  { label: '1M', days: 21 },
  { label: '3M', days: 63 },
  { label: '6M', days: 126 },
  { label: 'YTD', days: null },
  { label: '1Y', days: 252 },
  { label: '2Y', days: 504 },
];

export default function PortfolioBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const toast = useToast();
  const { live, prices, priceVersion, loadTickers, subscribeTickers } = useMarketData();

  const isNew = id === 'new';

  // Portfolio state
  const [name, setName]               = useState('New Portfolio');
  const [description, setDesc]        = useState('');
  const [benchmark, setBenchmark]     = useState('');
  const [holdings, setHoldings]       = useState([]);
  const [portfolioId, setPortfolioId] = useState(isNew ? crypto.randomUUID() : id);
  const [createdAt, setCreatedAt]     = useState(null);
  const [drip, setDrip]               = useState(true);   // dividend reinvestment
  const [cashPercent, setCashPercent] = useState(0);      // % held as cash
  const [startingValue, setStartingValue] = useState(100_000); // $ portfolio size
  const [weightHistory, setWeightHistory] = useState([]);      // log of weight changes
  const [historyOpen, setHistoryOpen]     = useState(false);   // history panel toggle
  const [pieOpen, setPieOpen]             = useState(true);    // allocation wheel toggle
  const [wheelIdx, setWheelIdx]           = useState(-1);      // -1 = current, 0..N = history snapshots
  const [shareUrl, setShareUrl]           = useState(null);    // generated share link
  const [erOpen, setErOpen]               = useState(false);   // ER breakdown toggle
  const [yieldOpen, setYieldOpen]         = useState(false);   // yield breakdown toggle
  const [showInvite, setShowInvite]       = useState(false);   // invite client modal
  const [inviteEmail, setInviteEmail]     = useState('');
  const [inviteUrl, setInviteUrl]         = useState(null);
  const [linkedClient, setLinkedClient]   = useState(null);  // { email, accepted } linked via invite
  const [summaryTicker, setSummaryTicker] = useState(null);

  // Schwab brokerage integration state
  const [schwabAccountHash, setSchwabAccountHash] = useState(null);
  const [schwabPositions, setSchwabPositions]     = useState(null);  // { totalValue, positions }
  const hasSchwab = !!schwabAccountHash && !!schwabPositions;
  const [allPortfolios, setAllPortfolios]         = useState([]);

  // Collapsible section toggles
  const [holdingsOpen, setHoldingsOpen]     = useState(true);
  const [allocBreakOpen, setAllocBreakOpen] = useState(true);
  const [perfChartOpen, setPerfChartOpen]   = useState(true);
  const [holdingsPerfOpen, setHoldingsPerfOpen] = useState(true);
  const [perfSummaryOpen, setPerfSummaryOpen]   = useState(true);
  const [liveSnapOpen, setLiveSnapOpen]         = useState(true);
  const [allocBarOpen, setAllocBarOpen]         = useState(true);

  // UI state
  const [showUnlinkClient, setShowUnlinkClient] = useState(false);
  const [showDelete, setShowDelete]         = useState(false);
  const [showRebalance, setShowRebalance]   = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [weightErrors, setWeightErrors]     = useState({});
  const [realReturns, setRealReturns]       = useState(null);   // real Finnhub candle-based returns
  const [realRiskMetrics, setRealRiskMetrics] = useState(null); // real candle-based risk metrics

  // Load existing portfolio
  useEffect(() => {
    if (!isNew && user) {
      const all = getPortfolios(user.id);
      setAllPortfolios(all);
      const p = all.find((x) => x.id === id);
      if (p) {
        setName(p.name);
        setDesc(p.description ?? '');
        setBenchmark(p.primary_benchmark ?? '');
        setHoldings(p.holdings ?? []);
        setPortfolioId(p.id);
        setCreatedAt(p.created_at ?? null);
        setDrip(p.drip_enabled ?? true);
        setCashPercent(p.cash_percent ?? 0);
        setStartingValue(p.starting_value ?? 100_000);
        setWeightHistory(p.weight_history ?? []);
        setSchwabAccountHash(p.schwab_account_hash ?? null);
      }
    }
  }, [id, user, isNew]);

  // Look up which client is linked to this portfolio (for advisor display)
  useEffect(() => {
    if (isNew || !portfolioId || role === 'client') return;
    getLinkedClient(portfolioId).then((info) => { setLinkedClient(info || null); });
  }, [portfolioId, isNew, role]);

  // Load + subscribe real-time prices whenever holdings or benchmark change
  useEffect(() => {
    if (!live) return;
    const tickers = holdings.map((h) => h.ticker);
    if (benchmark) tickers.push(benchmark);
    if (!tickers.length) return;
    loadTickers(tickers);
    const unsub = subscribeTickers(tickers);
    return unsub;
  }, [live, holdings, benchmark, loadTickers, subscribeTickers]);

  // ── Performance auto-refresh ────────────────────────────────────────────────
  // Fetches real candle-based returns on mount + on a user-configurable interval.
  // Also exposes a manual trigger via refreshPerformance().
  const [perfRefreshing, setPerfRefreshing] = useState(false);
  const [lastPerfRefresh, setLastPerfRefresh] = useState(null);
  const [perfRefreshKey, setPerfRefreshKey]   = useState(0); // bump to force re-fetch

  const refreshPerformance = useCallback(() => {
    clearMarketCaches();
    setPerfRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!live || !holdings.length) return;
    let cancelled = false;
    setPerfRefreshing(true);
    Promise.all([
      getRealPerformanceReturns(holdings, benchmark || null),
      getRealRiskMetrics(holdings, benchmark || null, '1Y'),
    ]).then(([data, riskData]) => {
      if (cancelled) return;
      if (data) {
        setRealReturns(data);
        setLastPerfRefresh(new Date());
      } else {
        console.warn('[Finnhub] getRealPerformanceReturns returned null — check API key & rate limits');
      }
      if (riskData) setRealRiskMetrics(riskData);
    }).catch((err) => {
      console.warn('[Finnhub] Performance fetch error:', err.message);
    }).finally(() => {
      if (!cancelled) setPerfRefreshing(false);
    });
    return () => { cancelled = true; };
  }, [live, holdings, benchmark, perfRefreshKey]);

  // Interval-based auto-refresh driven by snapshot_refresh_interval setting
  useEffect(() => {
    if (!live || !holdings.length) return;
    const intervalSec = user ? getSettings(user.id).snapshot_refresh_interval : 60;
    if (!intervalSec || intervalSec <= 0) return; // 0 = Manual only
    const id = setInterval(refreshPerformance, intervalSec * 1000);
    return () => clearInterval(id);
  }, [live, holdings.length, user, refreshPerformance]);

  // Derived
  const totalWeight = holdings.reduce((s, h) => s + parseFloat(h.weight_percent || 0), 0);
  const isFullyAllocated = Math.abs(totalWeight - 100) < 0.01;
  const status = getPortfolioStatus({ holdings, primary_benchmark: benchmark });

  // Drifted weights: what each holding's actual weight is given price movements since entry
  const currentWeights = useMemo(() => {
    if (!holdings.length) return {};
    const rows = holdings.map((h) => {
      const currentPrice = (live && prices[h.ticker]?.price) || h.last_price;
      const entryPrice   = h.entry_price ?? h.last_price;
      const ratio = (entryPrice && entryPrice > 0) ? currentPrice / entryPrice : 1;
      return { ticker: h.ticker, targetWeight: h.weight_percent, ratio };
    });
    const denom = rows.reduce((s, r) => s + (r.targetWeight / 100) * r.ratio, 0);
    if (!denom || !isFinite(denom)) return {};
    return Object.fromEntries(rows.map((r) => [r.ticker, {
      driftedWeight: parseFloat(((r.targetWeight / 100) * r.ratio / denom * 100).toFixed(2)),
      ratio: r.ratio,
    }]));
  }, [holdings, prices, live]);

  // Current estimated portfolio value in dollars
  // When Schwab is linked, use real account value; otherwise estimate from starting_value
  const currentPortfolioValue = useMemo(() => {
    if (schwabPositions?.totalValue != null) return schwabPositions.totalValue;
    if (!holdings.length) return startingValue;
    const investedFrac = 1 - cashPercent / 100;
    const growthFactor = holdings.reduce(
      (s, h) => s + (h.weight_percent / 100) * (currentWeights[h.ticker]?.ratio ?? 1), 0
    );
    return startingValue * (growthFactor * investedFrac + cashPercent / 100);
  }, [holdings, currentWeights, startingValue, cashPercent, schwabPositions]);

  // Show Rebalance button when any holding has drifted ≥ 0.5% from its target
  const needsRebalance = useMemo(() =>
    holdings.some((h) =>
      Math.abs((currentWeights[h.ticker]?.driftedWeight ?? h.weight_percent) - h.weight_percent) >= 0.5
    ), [holdings, currentWeights]
  );

  // ── Holdings mutations ──────────────────────────────────────────────────────
  function addTicker(instrument) {
    // Validate ticker format: 1-15 uppercase alphanumeric chars, dots, hyphens
    if (!instrument.ticker || !/^[A-Z0-9.\-]{1,15}$/i.test(instrument.ticker)) return;
    if (holdings.find((h) => h.ticker === instrument.ticker)) return;
    // Capture entry price at time of adding — used for drift / rebalance calculations
    const entryPrice = (live && prices[instrument.ticker]?.price)
      ? prices[instrument.ticker].price
      : (instrument.last_price ?? 0);
    setHoldings((prev) => [
      ...prev,
      {
        ticker: instrument.ticker,
        name: instrument.name,
        type: instrument.type,
        last_price: instrument.last_price,
        entry_price: entryPrice,
        weight_percent: 0,
        category: 'Core',
      },
    ]);
  }

  function removeHolding(ticker) {
    setHoldings((prev) => prev.filter((h) => h.ticker !== ticker));
  }

  function updateWeight(ticker, raw) {
    const val = raw === '' ? '' : parseFloat(raw);
    setHoldings((prev) =>
      prev.map((h) => (h.ticker === ticker ? { ...h, weight_percent: val === '' ? 0 : isNaN(val) ? h.weight_percent : val } : h))
    );
    // Validate
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0 || v > 100) {
      setWeightErrors((e) => ({ ...e, [ticker]: 'Must be 0–100' }));
    } else {
      setWeightErrors((e) => { const n = { ...e }; delete n[ticker]; return n; });
    }
  }

  function updateCategory(ticker, cat) {
    setHoldings((prev) => prev.map((h) => h.ticker === ticker ? { ...h, category: cat } : h));
  }

  function normalize() {
    const safeTotal = holdings.reduce((s, h) => s + (Number(h.weight_percent) || 0), 0);
    if (!safeTotal || !isFinite(safeTotal)) return;
    setHoldings((prev) =>
      prev.map((h) => ({
        ...h,
        weight_percent: parseFloat((((Number(h.weight_percent) || 0) / safeTotal) * 100).toFixed(4)),
      }))
    );
    setWeightErrors({});
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  function handleSave() {
    if (!name.trim() || name.length > 200) return;
    if (startingValue && (!Number.isFinite(startingValue) || startingValue < 0 || startingValue > 1_000_000_000)) return;
    if (cashPercent < 0 || cashPercent > 100) return;
    setSaving(true);

    // ── Weight history diffing ────────────────────────────────────────────────
    const allSaved   = getPortfolios(user.id);
    const savedPortf = allSaved.find((p) => p.id === portfolioId);
    const prevHistory = savedPortf?.weight_history ?? weightHistory;

    let newEvent = null;
    if (!savedPortf) {
      // Brand-new portfolio
      newEvent = {
        id: crypto.randomUUID(), date: new Date().toISOString(), type: 'created',
        changes: holdings.map((h) => ({ ticker: h.ticker, from: null, to: h.weight_percent })),
      };
    } else {
      const prevMap = Object.fromEntries((savedPortf.holdings ?? []).map((h) => [h.ticker, h.weight_percent]));
      const changes = [];
      holdings.forEach((h) => {
        const prev = prevMap[h.ticker];
        if (prev == null) {
          changes.push({ ticker: h.ticker, from: null, to: h.weight_percent });
        } else if (Math.abs(prev - h.weight_percent) >= 0.01) {
          changes.push({ ticker: h.ticker, from: prev, to: h.weight_percent });
        }
      });
      (savedPortf.holdings ?? []).forEach((h) => {
        if (!holdings.find((c) => c.ticker === h.ticker))
          changes.push({ ticker: h.ticker, from: h.weight_percent, to: null });
      });
      if (changes.length) {
        const type = changes.every((c) => c.from == null)  ? 'holding_added'
                   : changes.every((c) => c.to   == null)  ? 'holding_removed'
                   : 'adjustment';
        newEvent = { id: crypto.randomUUID(), date: new Date().toISOString(), type, changes };
      }
    }
    const updatedHistory = newEvent ? [...prevHistory, newEvent] : prevHistory;
    if (newEvent) setWeightHistory(updatedHistory);

    const portfolio = {
      id:                  portfolioId,
      owner:               user.id,
      name:                name.trim(),
      description:         description.trim(),
      primary_benchmark:   benchmark || null,
      secondary_benchmarks: [],
      holdings,
      drip_enabled:        drip,
      cash_percent:        cashPercent,
      starting_value:      startingValue,
      created_at:          createdAt ?? new Date().toISOString(),
      weight_history:      updatedHistory,
      schwab_account_hash: schwabAccountHash || null,
    };
    savePortfolio(portfolio);
    logActivity(user.id, {
      portfolio_id: portfolioId,
      portfolio_name: portfolio.name,
      action_type: isNew ? 'Create' : 'Update',
      change_summary: `${isNew ? 'Created' : 'Updated'} "${portfolio.name}" (${holdings.length} holdings, ${totalWeight.toFixed(1)}% allocated)`,
    });
    setSaving(false);
    toast.success('Portfolio saved');
    if (isNew) navigate(`/portfolio/${portfolioId}`, { replace: true });
  }

  // ── Duplicate ───────────────────────────────────────────────────────────────
  // Copies weights, structure, and all settings — resets historical values, gains, and dates.
  function handleDuplicate() {
    const newId = crypto.randomUUID();
    const dup = {
      id: newId,
      owner: user.id,
      name: `${name} (Copy)`,
      description,
      primary_benchmark: benchmark || null,
      secondary_benchmarks: [],
      starting_value: startingValue,       // preserve starting value
      cash_percent: cashPercent,            // preserve cash allocation
      drip_enabled: drip,                   // preserve DRIP setting
      holdings: holdings.map((h) => {
        const currentPrice = (live && prices[h.ticker]?.price) || h.last_price;
        return {
          ticker: h.ticker,
          name: h.name,
          type: h.type,
          exchange: h.exchange,
          category: h.category || 'Core',   // ensure role is always copied
          weight_percent: h.weight_percent,
          last_price: currentPrice,
          entry_price: currentPrice,  // Reset entry to current live price
          expense_ratio: h.expense_ratio,
          div_yield: h.div_yield,
        };
      }),
      weight_history: [],
      created_at: new Date().toISOString(),
    };
    savePortfolio(dup);
    logActivity(user.id, {
      portfolio_id: newId,
      portfolio_name: dup.name,
      action_type: 'Duplicate',
      change_summary: `Duplicated "${name}" → "${dup.name}"`,
    });
    navigate(`/portfolio/${newId}`);
  }

  // ── Delete ──────────────────────────────────────────────────────────────────
  function handleDelete() {
    logActivity(user.id, {
      portfolio_id: portfolioId,
      portfolio_name: name,
      action_type: 'Delete',
      change_summary: `Deleted "${name}"`,
    });
    deletePortfolios([portfolioId], user.id);
    navigate('/');
  }

  // ── Rebalance ────────────────────────────────────────────────────────────────
  // Resets each holding's entry_price to its current price, zeroing the drift.
  // Logs a 'rebalance' event showing before (drifted) → after (target) weights.
  function handleRebalance() {
    const rebalanceEvent = {
      id: crypto.randomUUID(),
      date: new Date().toISOString(),
      type: 'rebalance',
      changes: holdings.map((h) => ({
        ticker: h.ticker,
        from: parseFloat((currentWeights[h.ticker]?.driftedWeight ?? h.weight_percent).toFixed(2)),
        to: h.weight_percent,
      })),
    };
    const rebalancedHoldings = holdings.map((h) => ({
      ...h,
      entry_price: (live && prices[h.ticker]?.price) || h.last_price,
    }));
    const newHistory = [...weightHistory, rebalanceEvent];
    const portfolio = {
      id:                  portfolioId,
      owner:               user.id,
      name:                name.trim(),
      description:         description.trim(),
      primary_benchmark:   benchmark || null,
      secondary_benchmarks: [],
      holdings:            rebalancedHoldings,
      drip_enabled:        drip,
      cash_percent:        cashPercent,
      starting_value:      startingValue,
      created_at:          createdAt ?? new Date().toISOString(),
      weight_history:      newHistory,
    };
    savePortfolio(portfolio);
    setHoldings(rebalancedHoldings);
    setWeightHistory(newHistory);
    setShowRebalance(false);
    toast.success('Portfolio rebalanced to target weights');
  }

  // ── Share with Client ────────────────────────────────────────────────────────
  async function handleShare() {
    if (isNew) {
      toast.error('Save the portfolio first before sharing.');
      return;
    }
    const snap = {
      id: portfolioId, name, description,
      primary_benchmark: benchmark, holdings,
      starting_value: startingValue, created_at: createdAt,
    };
    try {
      const token = await createShareToken(user.id, snap);
      const url = `${window.location.origin}/share/${token}`;
      setShareUrl(url);
      navigator.clipboard?.writeText(url);
      toast.success('Share link copied to clipboard!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate share link');
    }
  }

  // ── Invite Client ────────────────────────────────────────────────────────────
  async function handleInvite() {
    if (!inviteEmail.trim()) {
      toast.error('Please enter a client email address.');
      return;
    }
    // If the portfolio is new / unsaved, save it first
    if (isNew || !portfolioId) {
      toast.error('Save the portfolio first before inviting a client.');
      return;
    }
    // Build a minimal snapshot — strip heavy fields to keep URL short
    const snap = {
      id: portfolioId,
      owner: user.id,
      name,
      description: (description || '').slice(0, 200),
      primary_benchmark: benchmark,
      holdings: holdings.map(h => ({
        ticker: h.ticker,
        name: (h.name || '').replace(/[^\x20-\x7E]/g, ''), // ASCII-safe
        type: h.type,
        weight_percent: h.weight_percent,
        category: h.category || 'Core',
        last_price: h.last_price,
      })),
      starting_value: startingValue,
      cash_percent: cashPercent,
      drip_enabled: drip,
      created_at: createdAt,
    };
    try {
      const token = await inviteClient(user.id, inviteEmail.trim(), [portfolioId], snap);
      const url = `${window.location.origin}/invite/${token}`;
      setInviteUrl(url);
      try { await navigator.clipboard?.writeText(url); } catch { /* ok */ }
      toast.success('Invite link created! Link copied to clipboard.');
    } catch (e) {
      console.error('Invite creation error:', e);
      toast.error(`Failed to create invite: ${e.message || 'Unknown error. Check console for details.'}`);
    }
  }

  // ── Live snapshot daily moves ────────────────────────────────────────────────
  // Uses Schwab day P&L when linked, otherwise Finnhub prices
  const topHoldings = [...holdings]
    .sort((a, b) => b.weight_percent - a.weight_percent)
    .slice(0, 5)
    .map((h) => {
      const sp = hasSchwab ? schwabPositions?.positions?.find(p => p.ticker === h.ticker) : null;
      const realQuote = live ? prices[h.ticker] : null;
      return {
        ...h,
        displayPrice:  sp ? (sp.marketValue / (sp.quantity || 1)) : (realQuote?.price ?? h.last_price),
        dailyChange:   sp?.dayPLPercent != null ? sp.dayPLPercent : (realQuote?.changePercent ?? null),
        marketValue:   sp?.marketValue ?? null,
        isLive:        !!sp || !!realQuote,
      };
    });

  // Portfolio-level 1D return — use Schwab day P&L if linked
  const portfolioReturn1D = (() => {
    if (hasSchwab && holdings.length > 0) {
      // Sum Schwab day P&L across all positions relative to total value
      const totalDayPL = schwabPositions?.positions?.reduce((sum, p) => sum + (p.dayPL || 0), 0);
      const totalVal = schwabPositions.totalValue;
      return totalVal > 0 ? (totalDayPL / totalVal) * 100 : 0;
    }
    if (live && holdings.length > 0) {
      return holdings.reduce((sum, h) => {
        const cp = prices[h.ticker]?.changePercent;
        return sum + (cp != null ? cp * (h.weight_percent / 100) : 0);
      }, 0);
    }
    return null;
  })();

  const benchmarkReturn1D = benchmark
    ? (live && prices[benchmark]?.changePercent != null
        ? prices[benchmark].changePercent
        : null)
    : null;

  // Weighted expense ratio and dividend yield (adjusted for cash allocation)
  const investedFraction = 1 - cashPercent / 100;
  const weightedER = holdings.reduce((sum, h) => {
    const inst = INSTRUMENTS.find((i) => i.ticker === h.ticker);
    return sum + (h.weight_percent / 100) * (inst?.expense_ratio ?? 0);
  }, 0) * investedFraction;

  const weightedYield = holdings.reduce((sum, h) => {
    const inst = INSTRUMENTS.find((i) => i.ticker === h.ticker);
    return sum + (h.weight_percent / 100) * (inst?.div_yield ?? 0);
  }, 0) * investedFraction;

  return (
    <div className="max-w-screen-xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
      {/* Top toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <h1 className="text-lg sm:text-xl font-bold text-slate-900">Portfolio Builder</h1>
          <StatusBadge status={status} totalWeight={totalWeight} />
          {linkedClient && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                linkedClient.accepted
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
              title={linkedClient.accepted ? 'Client account set up' : 'Client has not yet accepted invite'}
            >
              <User className="w-3 h-3" /> {linkedClient.email}
              <button
                type="button"
                onClick={() => setShowUnlinkClient(true)}
                className="ml-0.5 hover:text-red-600 transition-colors"
                title="Remove client from this portfolio"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <button className="btn-ghost" onClick={() => navigate('/')}>← <span className="hidden sm:inline">Dashboard</span></button>
          <button className="btn-secondary flex items-center gap-1.5" onClick={handleShare} disabled={isNew}>
            <Share2 className="w-4 h-4" /><span className="hidden sm:inline">Share</span>
          </button>
          {role !== 'client' && (
            <button className="btn-secondary flex items-center gap-1.5" onClick={() => { setShowInvite(true); setInviteUrl(null); setInviteEmail(''); }} disabled={isNew}>
              <Plus className="w-4 h-4" /><span className="hidden sm:inline">Invite Client</span>
            </button>
          )}
          {isSchwabConfigured() && (
            <SchwabLinkButton
              userId={user?.id}
              portfolioId={portfolioId}
              schwabAccountHash={schwabAccountHash}
              allPortfolios={allPortfolios}
              onAccountLinked={(hash) => {
                setSchwabAccountHash(hash);
                // Auto-save the link immediately so it persists
                if (portfolioId && user) {
                  const p = {
                    id: portfolioId, owner: user.id, name: name.trim(),
                    description: description.trim(), primary_benchmark: benchmark || null,
                    secondary_benchmarks: [], holdings, drip_enabled: drip,
                    cash_percent: cashPercent, starting_value: startingValue,
                    created_at: createdAt ?? new Date().toISOString(),
                    weight_history: weightHistory, schwab_account_hash: hash,
                  };
                  savePortfolio(p);
                  setAllPortfolios(prev => prev.map(x => x.id === portfolioId ? { ...x, schwab_account_hash: hash } : x));
                }
              }}
              onAccountUnlinked={() => {
                setSchwabAccountHash(null);
                setSchwabPositions(null);
                // Auto-save the unlink immediately
                if (portfolioId && user) {
                  const p = {
                    id: portfolioId, owner: user.id, name: name.trim(),
                    description: description.trim(), primary_benchmark: benchmark || null,
                    secondary_benchmarks: [], holdings, drip_enabled: drip,
                    cash_percent: cashPercent, starting_value: startingValue,
                    created_at: createdAt ?? new Date().toISOString(),
                    weight_history: weightHistory, schwab_account_hash: null,
                  };
                  savePortfolio(p);
                  setAllPortfolios(prev => prev.map(x => x.id === portfolioId ? { ...x, schwab_account_hash: null } : x));
                }
              }}
              onPositionsLoaded={(data) => { setSchwabPositions(data); }}
            />
          )}
          <button className="btn-secondary" onClick={handleDuplicate} disabled={isNew}>
            <Copy className="w-4 h-4" /><span className="hidden sm:inline">Duplicate</span>
          </button>
          <button className="btn-danger" onClick={() => setShowDelete(true)} disabled={isNew}>
            <Trash2 className="w-4 h-4" /><span className="hidden sm:inline">Delete</span>
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {shareUrl && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800 flex items-center gap-2">
          <span className="font-mono truncate flex-1">{shareUrl}</span>
          <button
            onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success('Copied!'); }}
            className="shrink-0 underline"
          >
            Copy
          </button>
          <button onClick={() => setShareUrl(null)} className="shrink-0 text-green-600 hover:text-green-800 ml-1">✕</button>
        </div>
      )}

      <div className="space-y-4 sm:space-y-6">
        {/* Main column — full width so holdings table is never squeezed */}
        <div className="space-y-4 sm:space-y-6">

          {/* Schwab Account Summary — real brokerage data */}
          {hasSchwab && (
            <div className="card p-5 border-emerald-200 bg-emerald-50/30">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="section-title text-emerald-800">Schwab Account Overview</h2>
                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Live</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-white p-3 text-center border border-emerald-100">
                  <div className="text-xs text-slate-500 mb-1">Account Value</div>
                  <div className="text-lg font-bold text-emerald-700">
                    ${Math.round(schwabPositions.totalValue).toLocaleString('en-US')}
                  </div>
                </div>
                <div className="rounded-lg bg-white p-3 text-center border border-emerald-100">
                  <div className="text-xs text-slate-500 mb-1">Model Value</div>
                  <div className="text-lg font-bold text-slate-700">
                    ${Math.round(startingValue).toLocaleString('en-US')}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">starting value</div>
                </div>
                <div className="rounded-lg bg-white p-3 text-center border border-emerald-100">
                  <div className="text-xs text-slate-500 mb-1">Positions</div>
                  <div className="text-lg font-bold text-slate-700">
                    {schwabPositions?.positions?.length}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {holdings.length} in model
                  </div>
                </div>
                <div className="rounded-lg bg-white p-3 text-center border border-emerald-100">
                  <div className="text-xs text-slate-500 mb-1">Max Drift</div>
                  {(() => {
                    const drifts = holdings.map(h => {
                      const sp = schwabPositions?.positions?.find(p => p.ticker === h.ticker);
                      return sp ? Math.abs(sp.actualWeight - h.weight_percent) : 0;
                    });
                    const maxDrift = Math.max(0, ...drifts);
                    const driftColor = maxDrift <= 1 ? 'text-green-600' : maxDrift <= 3 ? 'text-amber-500' : 'text-red-500';
                    return (
                      <>
                        <div className={`text-lg font-bold ${driftColor}`}>{maxDrift.toFixed(1)}%</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {maxDrift <= 1 ? 'on target' : maxDrift <= 3 ? 'minor drift' : 'needs rebalance'}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              {/* Quick allocation comparison */}
              {holdings.length > 0 && (
                <div className="mt-4 pt-3 border-t border-emerald-100">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Target vs Actual Allocation</h3>
                  <div className="space-y-1.5">
                    {holdings
                      .sort((a, b) => b.weight_percent - a.weight_percent)
                      .slice(0, 10)
                      .map(h => {
                        const sp = schwabPositions?.positions?.find(p => p.ticker === h.ticker);
                        const actual = sp?.actualWeight ?? 0;
                        const target = h.weight_percent;
                        const drift = actual - target;
                        const driftColor = Math.abs(drift) <= 1 ? 'text-green-600' : Math.abs(drift) <= 3 ? 'text-amber-500' : 'text-red-500';
                        return (
                          <div key={h.ticker} className="flex items-center gap-2 text-xs">
                            <span className="w-14 font-mono font-semibold text-slate-700 text-right">{h.ticker}</span>
                            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden relative">
                              {/* Target bar */}
                              <div
                                className="absolute inset-y-0 left-0 bg-blue-200 rounded-full"
                                style={{ width: `${Math.min(target, 100)}%` }}
                              />
                              {/* Actual bar */}
                              <div
                                className="absolute inset-y-0 left-0 bg-emerald-400 rounded-full opacity-70"
                                style={{ width: `${Math.min(actual, 100)}%` }}
                              />
                            </div>
                            <span className="w-12 text-right font-mono text-slate-500">{target.toFixed(1)}%</span>
                            <span className="w-12 text-right font-mono text-emerald-600">{actual.toFixed(1)}%</span>
                            <span className={`w-14 text-right font-mono font-semibold ${driftColor}`}>
                              {drift > 0 ? '+' : ''}{drift.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-1 pl-16">
                      <span className="inline-block w-3 h-2 bg-blue-200 rounded-sm" /> Target
                      <span className="inline-block w-3 h-2 bg-emerald-400 rounded-sm ml-2" /> Actual (Schwab)
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Portfolio meta */}
          <div className="card p-5 space-y-4">
            <h2 className="section-title">Portfolio Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Portfolio name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Primary Benchmark
                  <span className="ml-1 text-xs font-normal text-slate-400">ETFs (SPY, QQQ) have real data; indices (SPX) may not</span>
                </label>
                <select
                  className="input"
                  value={benchmark}
                  onChange={(e) => setBenchmark(e.target.value)}
                >
                  <option value="">— None —</option>
                  {BENCHMARKS.map((b) => (
                    <option key={b} value={b}>
                      {BENCHMARK_META[b]?.label ?? b}{['SPY','QQQ','IWM','DIA','EFA','AGG'].includes(b) ? ' (ETF — real data)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={description}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Optional description"
              />
            </div>

            {/* Starting value + Starting date row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 border-t border-slate-100">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Starting Value
                  <span className="ml-1 text-xs font-normal text-slate-400">initial portfolio size</span>
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400 text-sm font-medium">$</span>
                  <input
                    type="number"
                    min={1000}
                    step={1000}
                    className="input text-right font-mono"
                    value={startingValue}
                    onChange={(e) => setStartingValue(Math.max(1000, parseFloat(e.target.value) || 100_000))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Starting Date
                  <span className="ml-1 text-xs font-normal text-slate-400">sets "Since Creation" range</span>
                </label>
                <input
                  type="date"
                  className="input"
                  value={createdAt ? createdAt.slice(0, 10) : ''}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    if (e.target.value) {
                      setCreatedAt(new Date(e.target.value + 'T12:00:00').toISOString());
                    } else {
                      setCreatedAt(null);
                    }
                  }}
                />
              </div>
            </div>

            {/* Cash + DRIP row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 border-t border-slate-100">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Cash Reserve
                  <span className="ml-1 text-xs font-normal text-slate-400">% of portfolio held as cash</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    className="input w-24 text-right font-mono"
                    value={cashPercent}
                    onChange={(e) => setCashPercent(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                  />
                  <span className="text-slate-400 text-sm">%</span>
                  {cashPercent > 0 && (
                    <span className="text-xs text-blue-600 font-medium flex items-center gap-0.5">
                      <DollarSign className="w-3 h-3" />{cashPercent}% cash earning ~5% p.a.
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dividend Reinvestment</label>
                <label className="flex items-center gap-3 cursor-pointer mt-1.5">
                  <div
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${drip ? 'bg-blue-600' : 'bg-slate-300'}`}
                    onClick={() => setDrip(!drip)}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${drip ? 'translate-x-4' : 'translate-x-1'}`} />
                  </div>
                  <span className="text-sm text-slate-700">
                    {drip ? 'DRIP enabled — dividends auto-reinvested' : 'DRIP off — dividends go to cash'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Holdings table */}
          <div className="card p-5">
            <button
              className="flex items-center justify-between w-full mb-4"
              onClick={() => setHoldingsOpen((o) => !o)}
            >
              <h2 className="section-title">Holdings ({holdings.length})</h2>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${holdingsOpen ? 'rotate-180' : ''}`} />
            </button>
            {holdingsOpen && <div className="flex items-center justify-end gap-2 mb-4">
                {!isFullyAllocated && holdings.length > 0 && (
                  <button className="btn-secondary text-xs" onClick={normalize}>
                    Normalize to 100%
                  </button>
                )}
                {needsRebalance && (
                  <button className="btn-secondary text-xs" onClick={() => setShowRebalance(true)}>
                    ⟳ Rebalance
                  </button>
                )}
                <TickerSearch
                  existingTickers={holdings.map((h) => h.ticker)}
                  onAdd={addTicker}
                />
              </div>
            }

            {holdingsOpen && (holdings.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                Use the search above to add tickers to this portfolio.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="th pl-0">Symbol</th>
                        <th className="th hidden md:table-cell">Name</th>
                        <th className="th hidden lg:table-cell">Type</th>
                        <th className="th hidden lg:table-cell">Role</th>
                        <th className="th text-right hidden sm:table-cell">Last Price</th>
                        <th className="th text-right">Target %</th>
                        <th className="th text-right hidden sm:table-cell">{hasSchwab ? 'Actual %' : 'Current %'}</th>
                        <th className="th text-right hidden sm:table-cell">{hasSchwab ? 'Market Value' : 'Est. Value'}</th>
                        {hasSchwab && <th className="th text-right hidden sm:table-cell">Drift</th>}
                        <th className="th w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {holdings.map((h) => (
                        <tr key={h.ticker} className="group">
                          <td
                            className="td pl-0 font-semibold text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                            onClick={() => setSummaryTicker(h.ticker)}
                            title="Click for ticker summary"
                          >
                            {h.ticker}
                          </td>
                          <td className="td text-slate-600 max-w-xs truncate hidden md:table-cell">{h.name}</td>
                          <td className="td hidden lg:table-cell">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              h.type === 'ETF' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                            }`}>{h.type}</span>
                          </td>
                          <td className="td hidden lg:table-cell">
                            <div className="flex items-center gap-1">
                              {['Core', 'Tilt', 'Satellite'].map((cat) => {
                                const active = (h.category || 'Core') === cat;
                                const colors = {
                                  Core: active ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'text-slate-400 hover:bg-blue-50 hover:text-blue-600',
                                  Tilt: active ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'text-slate-400 hover:bg-violet-50 hover:text-violet-600',
                                  Satellite: active ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' : 'text-slate-400 hover:bg-amber-50 hover:text-amber-600',
                                };
                                return (
                                  <button
                                    key={cat}
                                    type="button"
                                    className={`text-xs px-2 py-1 rounded-full font-medium transition-all cursor-pointer ${colors[cat]}`}
                                    onClick={() => updateCategory(h.ticker, cat)}
                                  >
                                    {cat}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                          <td className="td text-right font-mono text-slate-700 hidden sm:table-cell">
                            {(() => {
                              const lp = live && prices[h.ticker]?.price
                                ? prices[h.ticker].price
                                : h.last_price;
                              const isReal = live && prices[h.ticker]?.price;
                              return (
                                <span>
                                  ${lp?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  {isReal && <span className="ml-1 text-green-500 text-xs">●</span>}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="td text-right">
                            <div className="flex items-center justify-end gap-1">
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                className={`input w-14 text-right font-mono text-sm ${weightErrors[h.ticker] ? 'border-red-400' : ''}`}
                                value={h.weight_percent}
                                onChange={(e) => updateWeight(h.ticker, e.target.value)}
                              />
                              <span className="text-slate-400 text-sm">%</span>
                            </div>
                            {weightErrors[h.ticker] && (
                              <p className="text-xs text-red-500 mt-0.5 text-right">{weightErrors[h.ticker]}</p>
                            )}
                          </td>
                          {/* Current/Actual weight — Schwab actual when linked, otherwise price-drift estimate */}
                          <td className="td text-right hidden sm:table-cell">
                            {(() => {
                              if (hasSchwab) {
                                const sp = schwabPositions?.positions?.find(p => p.ticker === h.ticker);
                                if (!sp) return <span className="text-slate-300 text-xs">—</span>;
                                const diff = sp.actualWeight - h.weight_percent;
                                return (
                                  <span className={`font-mono text-sm ${
                                    Math.abs(diff) < 0.5 ? 'text-slate-500'
                                      : diff > 0 ? 'text-green-600 font-semibold'
                                      : 'text-orange-500 font-semibold'
                                  }`}>
                                    {sp.actualWeight.toFixed(2)}%
                                  </span>
                                );
                              }
                              const drifted = currentWeights[h.ticker]?.driftedWeight;
                              if (drifted == null) return <span className="text-slate-300 text-xs">—</span>;
                              const diff = drifted - h.weight_percent;
                              return (
                                <span className={`font-mono text-sm ${
                                  Math.abs(diff) < 0.5 ? 'text-slate-500'
                                    : diff > 0 ? 'text-green-600 font-semibold'
                                    : 'text-orange-500 font-semibold'
                                }`}>
                                  {drifted.toFixed(2)}%
                                </span>
                              );
                            })()}
                          </td>
                          {/* Market Value — Schwab real value when linked, otherwise estimated */}
                          <td className="td text-right hidden sm:table-cell">
                            {(() => {
                              if (hasSchwab) {
                                const sp = schwabPositions?.positions?.find(p => p.ticker === h.ticker);
                                if (!sp) return <span className="text-slate-300 text-xs">—</span>;
                                return (
                                  <span className="font-mono text-sm text-emerald-700">
                                    ${Math.round(sp.marketValue).toLocaleString('en-US')}
                                  </span>
                                );
                              }
                              const drifted = currentWeights[h.ticker]?.driftedWeight;
                              if (drifted == null) return <span className="text-slate-300 text-xs">--</span>;
                              const dollarValue = currentPortfolioValue * (drifted / 100);
                              return (
                                <span className="font-mono text-sm text-slate-700">
                                  ${Math.round(dollarValue).toLocaleString('en-US')}
                                </span>
                              );
                            })()}
                          </td>
                          {/* Drift from target — only when Schwab linked */}
                          {hasSchwab && (() => {
                            const sp = schwabPositions?.positions?.find(p => p.ticker === h.ticker);
                            if (!sp) return <td className="td text-right text-slate-300 text-xs hidden sm:table-cell">—</td>;
                            const drift = sp.actualWeight - h.weight_percent;
                            const absDrift = Math.abs(drift);
                            const driftColor = absDrift <= 1 ? 'text-green-600' : absDrift <= 3 ? 'text-amber-500' : 'text-red-500';
                            return (
                              <td className={`td text-right font-mono text-sm font-semibold hidden sm:table-cell ${driftColor}`}>
                                {drift > 0 ? '+' : ''}{drift.toFixed(2)}%
                              </td>
                            );
                          })()}
                          <td className="td">
                            <button
                              className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                              onClick={() => removeHolding(h.ticker)}
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200">
                        <td className="td font-semibold text-slate-700">Total</td>
                        <td className="td hidden md:table-cell" />
                        <td className="td hidden lg:table-cell" />
                        <td className="td hidden lg:table-cell" />
                        <td className="td hidden sm:table-cell" />
                        <td className={`td text-right font-semibold font-mono text-base sm:text-lg ${
                          isFullyAllocated ? 'text-green-600' : Math.abs(totalWeight - 100) < 5 ? 'text-yellow-600' : 'text-red-500'
                        }`}>
                          {totalWeight.toFixed(2)}%
                        </td>
                        <td className="hidden sm:table-cell" />{/* Current/Actual % — no total needed */}
                        <td className={`td text-right font-semibold font-mono text-sm hidden sm:table-cell ${hasSchwab ? 'text-emerald-700' : 'text-slate-700'}`}>
                          ${Math.round(currentPortfolioValue).toLocaleString('en-US')}
                        </td>
                        {hasSchwab && <td className="td hidden sm:table-cell" />}{/* Drift — no total */}
                        <td />{/* Delete button column */}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Unmodeled Schwab positions — holdings in Schwab but not in the target model */}
                {hasSchwab && (() => {
                  const modelTickers = new Set(holdings.map(h => h.ticker));
                  const unmodeled = schwabPositions?.positions?.filter(
                    p => !modelTickers.has(p.ticker) && p.marketValue > 0
                  );
                  if (!unmodeled.length) return null;
                  return (
                    <div className="mt-3 p-3 bg-amber-50/60 border border-amber-200 rounded-lg">
                      <h4 className="text-xs font-semibold text-amber-700 mb-2">
                        Unmodeled Positions ({unmodeled.length})
                        <span className="ml-1 font-normal text-amber-500">in Schwab but not in target model</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {unmodeled.map(p => (
                          <span key={p.ticker} className="inline-flex items-center gap-1 text-xs bg-white px-2 py-1 rounded border border-amber-200 font-mono text-amber-800">
                            {p.ticker}
                            <span className="text-amber-500">{p.actualWeight.toFixed(1)}%</span>
                            <span className="text-amber-400">${Math.round(p.marketValue).toLocaleString('en-US')}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Weight error banner */}
                {!isFullyAllocated && holdings.length > 0 && (
                  <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded ${
                    Math.abs(totalWeight - 100) < 0.01
                      ? 'bg-green-50 text-green-700'
                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                  }`}>
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    Weights sum to {totalWeight.toFixed(2)}% — must equal 100% to be fully allocated.
                    <button className="ml-auto underline text-xs" onClick={normalize}>Normalize</button>
                  </div>
                )}
              </>
            ))}
          </div>

          {/* Allocation Breakdown (Core / Tilt / Satellite) */}
          {holdings.length > 0 && (() => {
            const CATS = ['Core', 'Tilt', 'Satellite'];
            const CAT_COLORS = { Core: 'bg-blue-500', Tilt: 'bg-violet-500', Satellite: 'bg-amber-500' };
            const hasAny = CATS.some((cat) =>
              holdings.some((h) => (h.category || 'Core') === cat && (h.weight_percent || 0) > 0)
            );
            if (!hasAny) return null;
            return (
              <div className="card p-5">
                <button className="flex items-center justify-between w-full" onClick={() => setAllocBreakOpen(o => !o)}>
                  <h2 className="section-title">Allocation Breakdown</h2>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${allocBreakOpen ? 'rotate-180' : ''}`} />
                </button>
                {allocBreakOpen && <div className="mt-3">{CATS.map((cat) => {
                  const w = holdings
                    .filter((h) => (h.category || 'Core') === cat)
                    .reduce((s, h) => s + (h.weight_percent || 0), 0);
                  if (w === 0) return null;
                  return (
                    <div key={cat} className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-500 w-16">{cat}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div
                          className={`${CAT_COLORS[cat]} h-2 rounded-full transition-all`}
                          style={{ width: `${Math.min(w, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-slate-700 w-10 text-right">{w.toFixed(1)}%</span>
                    </div>
                  );
                })}</div>}
              </div>
            );
          })()}

          {/* Performance chart */}
          <div className="card p-5">
            <button className="flex items-center justify-between w-full" onClick={() => setPerfChartOpen(o => !o)}>
              <h2 className="section-title">Performance Chart</h2>
              <div className="flex items-center gap-2">
                {cashPercent > 0 && (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                    {cashPercent}% cash · {drip ? 'DRIP on' : 'DRIP off'}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${perfChartOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {perfChartOpen && (
              <div className="mt-4">
                <PerformanceChart
                  holdings={holdings}
                  benchmarkTicker={benchmark || null}
                  createdAt={createdAt}
                  cashPercent={cashPercent}
                  drip={drip}
                />
              </div>
            )}
          </div>

          {/* Individual Holdings Performance */}
          {holdings.length > 1 && (
            <div className="card p-5">
              <button className="flex items-center justify-between w-full" onClick={() => setHoldingsPerfOpen(o => !o)}>
                <h2 className="section-title">Holdings Performance</h2>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${holdingsPerfOpen ? 'rotate-180' : ''}`} />
              </button>
              {holdingsPerfOpen && (
                <div className="mt-4">
                  <p className="text-xs text-slate-400 mb-3">Individual % return for each holding in the portfolio</p>
                  <HoldingsPerformanceChart holdings={holdings} />
                </div>
              )}
            </div>
          )}

          {/* Allocation Wheel (pie chart) — swipeable between current and historical snapshots */}
          {holdings.length > 0 && (
            <div className="card p-5">
              <button
                className="flex items-center justify-between w-full"
                onClick={() => setPieOpen((o) => !o)}
              >
                <h2 className="section-title">Allocation Wheel</h2>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${pieOpen ? 'rotate-180' : ''}`} />
              </button>
              {pieOpen && (() => {
                // Build historical snapshots from weight history
                const snapshots = [];
                let running = {}; // ticker → weight_percent
                for (const event of weightHistory) {
                  // Apply changes
                  for (const c of event.changes) {
                    if (c.to != null) running[c.ticker] = c.to;
                    else delete running[c.ticker];
                  }
                  const holdingsSnap = Object.entries(running)
                    .filter(([, w]) => w > 0)
                    .map(([ticker, weight_percent]) => ({
                      ticker,
                      weight_percent,
                      name: holdings.find((h) => h.ticker === ticker)?.name ?? ticker,
                    }));
                  snapshots.push({
                    date: event.date,
                    type: event.type,
                    holdings: holdingsSnap,
                  });
                }

                // All slides: historical snapshots + current
                const totalSlides = snapshots.length + 1;
                const activeIdx = wheelIdx === -1 ? totalSlides - 1 : Math.min(wheelIdx, totalSlides - 1);
                const isCurrentSlide = activeIdx === totalSlides - 1;
                const displayHoldings = isCurrentSlide ? holdings : snapshots[activeIdx]?.holdings ?? [];
                const displayDate = isCurrentSlide
                  ? 'Current'
                  : (snapshots[activeIdx]?.date && !isNaN(new Date(snapshots[activeIdx].date).getTime())
                      ? new Date(snapshots[activeIdx].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—');
                const displayType = isCurrentSlide ? null : snapshots[activeIdx]?.type;
                const typeLabels = {
                  created: 'Created', rebalance: 'Rebalanced', adjustment: 'Adjusted',
                  holding_added: 'Added', holding_removed: 'Removed',
                };

                return (
                  <div>
                    {/* Navigation controls */}
                    {snapshots.length > 0 && (
                      <div className="flex items-center justify-between mb-2 px-2">
                        <button
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-colors p-1"
                          disabled={activeIdx === 0}
                          onClick={() => setWheelIdx(activeIdx - 1)}
                          title="Previous snapshot"
                        >
                          ← Prev
                        </button>
                        <div className="text-center">
                          <span className="text-xs font-medium text-slate-700">{displayDate}</span>
                          {displayType && (
                            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              {typeLabels[displayType] ?? displayType}
                            </span>
                          )}
                          <div className="text-xs text-slate-400 mt-0.5">
                            {activeIdx + 1} of {totalSlides}
                          </div>
                        </div>
                        <button
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-colors p-1"
                          disabled={activeIdx === totalSlides - 1}
                          onClick={() => setWheelIdx(activeIdx + 1 === totalSlides - 1 ? -1 : activeIdx + 1)}
                          title="Next snapshot"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                    <AllocationPieChart holdings={displayHoldings} schwabPositions={hasSchwab ? schwabPositions : null} />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Performance summary */}
          <div className="card p-5">
            <button className="flex items-center justify-between w-full" onClick={() => setPerfSummaryOpen(o => !o)}>
              <h2 className="section-title">Performance Summary</h2>
              <div className="flex items-center gap-2">
                <span className={`text-xs italic ${realReturns ? 'text-green-500' : 'text-slate-400'}`}>
                  {realReturns
                    ? `● Live · real market data${Object.values(realReturns.portfolio).some(v => v == null) ? ' (partial)' : ''}`
                    : (live ? 'Loading real data…' : 'Market data unavailable')}
                </span>
                {live && (
                  <button
                    className="text-slate-400 hover:text-blue-600 transition-colors p-0.5 disabled:opacity-40"
                    disabled={perfRefreshing}
                    onClick={(e) => { e.stopPropagation(); refreshPerformance(); }}
                    title={lastPerfRefresh ? `Last refresh: ${lastPerfRefresh.toLocaleTimeString()}` : 'Refresh performance data'}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${perfRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${perfSummaryOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {perfSummaryOpen && <div className="mt-4">
            {(() => {
              // Portfolio age in approximate trading days (252/year)
              const portfolioAgeTradingDays = createdAt
                ? Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000 * (252 / 365))
                : 0;

              // Build the "Since Creation" tracker data — real data only
              const sinceCreationData = createdAt && holdings.length > 0 && realReturns?.portfolio?.['Since Inception'] != null ? (() => {
                const portfolioRet = Number(realReturns.portfolio['Since Inception']);
                const benchRet = realReturns?.benchmark?.['Since Inception'] != null
                  ? Number(realReturns.benchmark['Since Inception']) : null;
                return { portfolioRet, benchRet, outperf: benchRet !== null ? portfolioRet - benchRet : null };
              })() : null;

              return (
                <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                  {TIMEFRAMES.map(({ label, days }) => {
                    // Real market data only — no simulated fallbacks
                    const hasReal = realReturns?.portfolio?.[label] != null;
                    const portfolioRet = hasReal ? realReturns.portfolio[label] : null;
                    const benchRet = hasReal && realReturns.benchmark?.[label] != null
                      ? realReturns.benchmark[label] : null;
                    const outperf = (portfolioRet != null && benchRet != null) ? portfolioRet - benchRet : null;
                    const isBacktested = false;
                    return (
                      <div key={label} className={`rounded p-3 text-center ${hasReal ? 'bg-green-50/50' : isBacktested ? 'bg-amber-50/40 border border-amber-100' : 'bg-slate-50'}`}>
                        <div className="text-xs font-medium text-slate-500 mb-2">{label}</div>
                        <div className={`text-sm font-bold ${portfolioRet != null && portfolioRet > 0 ? 'text-green-600' : portfolioRet != null && portfolioRet < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                          {portfolioRet != null ? `${portfolioRet > 0 ? '+' : ''}${portfolioRet.toFixed(2)}%` : '—'}
                        </div>
                        {benchRet !== null && (
                          <div className="text-xs text-slate-400 mt-0.5">{BENCHMARK_META[benchmark]?.label ?? benchmark}: {benchRet > 0 ? '+' : ''}{benchRet.toFixed(2)}%</div>
                        )}
                        {outperf !== null && holdings.length > 0 && (
                          <div className={`text-xs mt-1 font-medium ${outperf > 0 ? 'text-green-600' : outperf < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                            {outperf > 0 ? '▲' : outperf < 0 ? '▼' : '='} {Math.abs(outperf).toFixed(2)}%
                          </div>
                        )}
                        {hasReal && (
                          <div className="text-xs text-green-500 mt-1 font-medium" title="Returns calculated from real market closing prices">
                            Real
                          </div>
                        )}
                        {!hasReal && holdings.length > 0 && (
                          <div
                            className="text-xs text-slate-400 italic mt-1 cursor-help"
                            title={realReturns ? 'Candle data unavailable for this timeframe' : 'Market data unavailable — connect Finnhub API'}
                          >
                            {realReturns ? 'Est.' : (isBacktested ? 'Backtested' : '')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Since Creation tracker */}
                  {sinceCreationData && (
                    <div className="rounded p-3 text-center bg-purple-50/50 border border-purple-100">
                      <div className="text-xs font-medium text-purple-600 mb-2">Since Creation</div>
                      <div className={`text-sm font-bold ${sinceCreationData.portfolioRet > 0 ? 'text-green-600' : sinceCreationData.portfolioRet < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                        {sinceCreationData.portfolioRet > 0 ? '+' : ''}{sinceCreationData.portfolioRet.toFixed(2)}%
                      </div>
                      {sinceCreationData.benchRet !== null && (
                        <div className="text-xs text-slate-400 mt-0.5">{BENCHMARK_META[benchmark]?.label ?? benchmark}: {sinceCreationData.benchRet > 0 ? '+' : ''}{sinceCreationData.benchRet.toFixed(2)}%</div>
                      )}
                      {sinceCreationData.outperf !== null && (
                        <div className={`text-xs mt-1 font-medium ${sinceCreationData.outperf > 0 ? 'text-green-600' : sinceCreationData.outperf < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                          {sinceCreationData.outperf > 0 ? '▲' : sinceCreationData.outperf < 0 ? '▼' : '='} {Math.abs(sinceCreationData.outperf).toFixed(2)}%
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Risk Metrics */}
                {holdings.length > 0 && (() => {
                  const metrics = realRiskMetrics?.portfolio ?? null;
                  const benchMetrics = realRiskMetrics?.benchmark ?? null;
                  const isReal = !!realRiskMetrics;
                  if (!metrics) return (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Risk Metrics (1Y)</h3>
                        <span className="text-xs text-amber-500">Data unavailable</span>
                      </div>
                      <p className="text-xs text-slate-400">Connect Finnhub API for real risk metrics.</p>
                    </div>
                  );
                  return (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Risk Metrics (1Y)</h3>
                        <span className={`text-xs ${isReal ? 'text-green-500' : 'text-slate-400'}`}>
                          {isReal ? '● Real' : 'Unavailable'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="rounded p-3 bg-slate-50 text-center">
                          <div className="text-xs text-slate-500 mb-1">Volatility</div>
                          <div className="text-sm font-bold text-slate-700">{metrics.volatility.toFixed(1)}%</div>
                          {benchMetrics && (
                            <div className="text-xs text-slate-400 mt-0.5">Bench: {benchMetrics.volatility.toFixed(1)}%</div>
                          )}
                        </div>
                        <div className="rounded p-3 bg-slate-50 text-center">
                          <div className="text-xs text-slate-500 mb-1">Max Drawdown</div>
                          <div className={`text-sm font-bold ${metrics.maxDrawdown < -10 ? 'text-red-500' : metrics.maxDrawdown < -5 ? 'text-amber-600' : 'text-slate-700'}`}>
                            {metrics.maxDrawdown.toFixed(1)}%
                          </div>
                          {benchMetrics && (
                            <div className="text-xs text-slate-400 mt-0.5">Bench: {benchMetrics.maxDrawdown.toFixed(1)}%</div>
                          )}
                        </div>
                        <div className="rounded p-3 bg-slate-50 text-center">
                          <div className="text-xs text-slate-500 mb-1">Sharpe Ratio</div>
                          <div className={`text-sm font-bold ${metrics.sharpe > 1 ? 'text-green-600' : metrics.sharpe > 0 ? 'text-slate-700' : 'text-red-500'}`}>
                            {metrics.sharpe.toFixed(2)}
                          </div>
                          {benchMetrics && (
                            <div className="text-xs text-slate-400 mt-0.5">Bench: {benchMetrics.sharpe.toFixed(2)}</div>
                          )}
                        </div>
                        <div className="rounded p-3 bg-slate-50 text-center">
                          <div className="text-xs text-slate-500 mb-1">Sortino Ratio</div>
                          <div className={`text-sm font-bold ${metrics.sortino > 1.5 ? 'text-green-600' : metrics.sortino > 0 ? 'text-slate-700' : 'text-red-500'}`}>
                            {metrics.sortino >= 99 ? '>99' : metrics.sortino.toFixed(2)}
                          </div>
                          {benchMetrics && (
                            <div className="text-xs text-slate-400 mt-0.5">Bench: {benchMetrics.sortino >= 99 ? '>99' : benchMetrics.sortino.toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                </>
              );
            })()}
          </div>}
          </div>

          {/* Weight History — collapsible log of all weight changes, rebalances, and additions */}
          {weightHistory.length > 0 && (
            <div className="card p-5">
              <button
                className="flex items-center justify-between w-full"
                onClick={() => setHistoryOpen((o) => !o)}
              >
                <h2 className="section-title">
                  Weight History
                  <span className="ml-2 text-xs font-normal text-slate-400">
                    ({weightHistory.length} event{weightHistory.length !== 1 ? 's' : ''})
                  </span>
                </h2>
                <span className="text-slate-400 text-sm">{historyOpen ? '▲' : '▼'}</span>
              </button>
              {historyOpen && (
                <div className="mt-4 space-y-3">
                  {[...weightHistory].reverse().map((event) => {
                    const typeConfig = {
                      created:         { icon: '★', color: 'text-blue-500',  label: 'Portfolio created' },
                      rebalance:       { icon: '⟳', color: 'text-green-600', label: 'Rebalanced to targets' },
                      adjustment:      { icon: '↕', color: 'text-amber-500', label: 'Weights adjusted' },
                      holding_added:   { icon: '+', color: 'text-blue-500',  label: 'Holdings added' },
                      holding_removed: { icon: '−', color: 'text-red-500',   label: 'Holdings removed' },
                    }[event.type] ?? { icon: '·', color: 'text-slate-400', label: event.type };
                    return (
                      <div key={event.id} className="flex gap-3 text-sm">
                        <div className={`flex-shrink-0 w-5 text-center font-bold mt-0.5 ${typeConfig.color}`}>
                          {typeConfig.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-medium text-slate-700">{typeConfig.label}</span>
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              {event.date && !isNaN(new Date(event.date).getTime()) ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {event.changes.map((c) => (
                              <span key={c.ticker} className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-600 font-mono">
                                {c.ticker}: {c.from != null ? `${c.from.toFixed(1)}%` : '—'} → {c.to != null ? `${c.to.toFixed(1)}%` : '—'}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Messages — advisor ↔ client communication */}
          {!isNew && (
            <MessagePanel
              portfolioId={portfolioId}
              userId={user.id}
              userEmail={user.email}
              userName={getSettings(user.id).display_name || ''}
              userRole={role}
            />
          )}
        </div>

        {/* Live Snapshot & Allocation — displayed below holdings for full-width layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Client approval status badge */}
          {/* Client connection + approval status (advisor view) */}
          {!isNew && role === 'advisor' && (() => {
            const clientStatus = getClientStatusForPortfolio(portfolioId);
            const approval = getLatestApproval(portfolioId);
            return (
              <div className="space-y-2">
                <div className={`p-3 rounded-lg text-sm ${
                  clientStatus.connected
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : clientStatus.pending
                    ? 'bg-blue-50 border border-blue-200 text-blue-700'
                    : 'bg-slate-50 border border-slate-200 text-slate-500'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      clientStatus.connected ? 'bg-green-500' : clientStatus.pending ? 'bg-blue-400 animate-pulse' : 'bg-slate-300'
                    }`} />
                    <span className="font-medium">
                      {clientStatus.connected
                        ? 'Client Connected'
                        : clientStatus.pending
                        ? 'Invite Pending'
                        : 'No Client Invited'}
                    </span>
                  </div>
                  {clientStatus.clientEmail && (
                    <span className="block text-xs opacity-75 mt-0.5 ml-4">
                      {clientStatus.clientEmail}
                      {clientStatus.acceptedAt && ` · joined ${new Date(clientStatus.acceptedAt).toLocaleDateString()}`}
                    </span>
                  )}
                </div>
                {approval && (
                  <div className={`p-3 rounded-lg text-sm ${
                    approval.type === 'approval'
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-amber-50 border border-amber-200 text-amber-700'
                  }`}>
                    <span className="font-medium">
                      {approval.type === 'approval' ? 'Client approved' : 'Changes requested'}
                    </span>
                    <span className="block text-xs opacity-75 mt-0.5">
                      {approval.sender_email} &middot; {new Date(approval.created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Client approval status badge (client view) */}
          {!isNew && role === 'client' && (() => {
            const raw = getLatestApproval(portfolioId);
            if (!raw || typeof raw !== 'object' || typeof raw.type !== 'string') return null;
            return (
              <div className={`p-3 rounded-lg text-sm ${
                raw.type === 'approval'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-amber-50 border border-amber-200 text-amber-700'
              }`}>
                <span className="font-medium">
                  {raw.type === 'approval' ? 'Client approved' : 'Changes requested'}
                </span>
                <span className="block text-xs opacity-75 mt-0.5">
                  {String(raw.sender_email || '')} &middot; {typeof raw.created_at === 'string' && !isNaN(new Date(raw.created_at).getTime()) ? new Date(raw.created_at).toLocaleDateString() : ''}
                </span>
              </div>
            );
          })()}
          <div className="card p-5">
            <button className="flex items-center justify-between w-full" onClick={() => setLiveSnapOpen(o => !o)}>
              <div className="flex items-center gap-2">
                <h2 className="section-title">Live Snapshot</h2>
                {live && (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse" />
                    Live
                  </span>
                )}
                {live && lastPerfRefresh && (
                  <span className="text-xs text-slate-400">
                    Updated {lastPerfRefresh.toLocaleTimeString()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {live && (
                  <button
                    className="text-slate-400 hover:text-blue-600 transition-colors p-0.5 disabled:opacity-40"
                    disabled={perfRefreshing}
                    onClick={(e) => { e.stopPropagation(); refreshPerformance(); }}
                    title="Refresh prices & performance"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${perfRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${liveSnapOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {liveSnapOpen && <>
            <p className="text-xs text-slate-400 mb-4 mt-2">
              Top holdings by weight · {live ? 'real-time prices' : 'prices unavailable'}
            </p>

            {topHoldings.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-6">Add holdings to see snapshot</div>
            ) : (
              <div className="space-y-3">
                {topHoldings.map((h) => (
                  <div key={h.ticker} className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm text-slate-900">{h.ticker}</div>
                      <div className="text-xs text-slate-400">{h.weight_percent.toFixed(1)}% weight</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-slate-700">
                        {h.displayPrice != null ? `$${h.displayPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                      </div>
                      <div className={`text-xs font-medium flex items-center gap-0.5 justify-end ${
                        h.dailyChange > 0 ? 'text-green-600' : h.dailyChange < 0 ? 'text-red-500' : 'text-slate-400'
                      }`}>
                        {h.dailyChange > 0 ? <TrendingUp className="w-3 h-3" /> : h.dailyChange < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                        {h.dailyChange != null && !isNaN(h.dailyChange) ? `${h.dailyChange > 0 ? '+' : ''}${h.dailyChange.toFixed(2)}%` : '—'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Portfolio-level snapshot */}
            {holdings.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Portfolio today</span>
                  <span className={`font-semibold ${
                    portfolioReturn1D > 0 ? 'text-green-600' : portfolioReturn1D < 0 ? 'text-red-500' : 'text-slate-500'
                  }`}>
                    {portfolioReturn1D > 0 ? '+' : ''}{portfolioReturn1D.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-500 cursor-help" title={hasSchwab ? 'Real account value from Schwab' : 'Estimated portfolio value based on starting value and market performance'}>
                    {hasSchwab ? 'Account Value' : 'Est. Value'}
                    {hasSchwab && <span className="ml-1 text-emerald-500 text-xs">● Schwab</span>}
                  </span>
                  <span className={`font-semibold ${hasSchwab ? 'text-emerald-700' : 'text-slate-800'}`}>
                    ${Math.round(currentPortfolioValue).toLocaleString('en-US')}
                  </span>
                </div>
                {benchmark && benchmarkReturn1D !== null && (
                  <div className="mt-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{BENCHMARK_META[benchmark]?.label ?? benchmark} today</span>
                      <span className={`font-semibold ${benchmarkReturn1D > 0 ? 'text-green-600' : benchmarkReturn1D < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                        {benchmarkReturn1D > 0 ? '+' : ''}{benchmarkReturn1D.toFixed(2)}%
                      </span>
                    </div>
                    {live && prices[benchmark]?.price != null && (
                      <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                        <span>Current / Prev Close</span>
                        <span className="font-mono">
                          ${prices[benchmark].price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {prices[benchmark]?.prevClose != null &&
                            ` / $${prices[benchmark].prevClose.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {/* Expandable ER + Yield breakdown cards */}
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-xs">
                  {/* Expense Ratio card */}
                  <div className="bg-gray-50 rounded p-2 cursor-pointer select-none" onClick={() => setErOpen((o) => !o)}>
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400">Wtd. Expense Ratio</p>
                      <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${erOpen ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="font-semibold text-gray-700 mt-0.5">{weightedER.toFixed(3)}%</p>
                    {erOpen && (() => {
                      const rows = holdings.map((h) => {
                        const inst = INSTRUMENTS.find((i) => i.ticker === h.ticker);
                        const rate = inst?.expense_ratio ?? null;
                        return { ticker: h.ticker, weight: h.weight_percent, rate,
                                 contribution: rate != null ? (h.weight_percent / 100) * rate * investedFraction : null };
                      });
                      if (!rows.length) return <p className="text-gray-400 italic mt-2">No holdings</p>;
                      return (
                        <table className="w-full mt-2 border-t border-gray-200">
                          <thead>
                            <tr className="text-gray-400">
                              <td className="py-1">Ticker</td>
                              <td className="py-1 text-right">Wt.</td>
                              <td className="py-1 text-right">ER</td>
                              <td className="py-1 text-right">Contrib.</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.ticker} className="border-t border-gray-100">
                                <td className="py-1 font-mono font-semibold text-gray-700">{r.ticker}</td>
                                <td className="py-1 text-right text-gray-500">{r.weight.toFixed(1)}%</td>
                                <td className="py-1 text-right text-gray-500">{r.rate != null ? `${r.rate.toFixed(3)}%` : <span className="text-gray-300">N/A</span>}</td>
                                <td className="py-1 text-right font-semibold text-gray-700">{r.contribution != null ? `${r.contribution.toFixed(4)}%` : <span className="text-gray-300">—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>

                  {/* Dividend Yield card */}
                  <div className="bg-gray-50 rounded p-2 cursor-pointer select-none" onClick={() => setYieldOpen((o) => !o)}>
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400">Wtd. Div. Yield</p>
                      <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${yieldOpen ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="font-semibold text-green-600 mt-0.5">{weightedYield.toFixed(2)}%</p>
                    {yieldOpen && (() => {
                      const rows = holdings.map((h) => {
                        const inst = INSTRUMENTS.find((i) => i.ticker === h.ticker);
                        const rate = inst?.div_yield ?? null;
                        return { ticker: h.ticker, weight: h.weight_percent, rate,
                                 contribution: rate != null ? (h.weight_percent / 100) * rate * investedFraction : null };
                      });
                      if (!rows.length) return <p className="text-gray-400 italic mt-2">No holdings</p>;
                      return (
                        <table className="w-full mt-2 border-t border-gray-200">
                          <thead>
                            <tr className="text-gray-400">
                              <td className="py-1">Ticker</td>
                              <td className="py-1 text-right">Wt.</td>
                              <td className="py-1 text-right">Yield</td>
                              <td className="py-1 text-right">Contrib.</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.ticker} className="border-t border-gray-100">
                                <td className="py-1 font-mono font-semibold text-gray-700">{r.ticker}</td>
                                <td className="py-1 text-right text-gray-500">{r.weight.toFixed(1)}%</td>
                                <td className="py-1 text-right text-green-600">{r.rate != null ? `${r.rate.toFixed(2)}%` : <span className="text-gray-300">N/A</span>}</td>
                                <td className="py-1 text-right font-semibold text-green-600">{r.contribution != null ? `${r.contribution.toFixed(3)}%` : <span className="text-gray-300">—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>}
          </div>

          {/* Weight breakdown */}
          {holdings.length > 0 && (
            <div className="card p-5">
              <button className="flex items-center justify-between w-full" onClick={() => setAllocBarOpen(o => !o)}>
                <h2 className="section-title">Allocation</h2>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${allocBarOpen ? 'rotate-180' : ''}`} />
              </button>
              {allocBarOpen && <div className="mt-3">
              <div className="space-y-2">
                {[...holdings].sort((a, b) => b.weight_percent - a.weight_percent).map((h) => (
                  <div key={h.ticker}>
                    <div className="flex justify-between text-xs text-slate-600 mb-0.5">
                      <span className="font-medium">{h.ticker}</span>
                      <span>{h.weight_percent.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${Math.min(h.weight_percent, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-slate-500">Total</span>
                <span className={`font-semibold ${isFullyAllocated ? 'text-green-600' : 'text-yellow-600'}`}>
                  {totalWeight.toFixed(2)}%
                </span>
              </div>
              </div>}
            </div>
          )}
        </div>
      </div>

      {showDelete && (
        <ConfirmModal
          title={`Delete "${name}"?`}
          message="All holdings and history for this portfolio will be permanently removed."
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {showRebalance && (
        <ConfirmModal
          title="Rebalance to target weights?"
          message="Entry prices will be updated to current market prices, resetting drift back to zero. A rebalance event will be logged in the weight history."
          confirmLabel="Rebalance"
          onConfirm={handleRebalance}
          onCancel={() => setShowRebalance(false)}
        />
      )}

      {showUnlinkClient && linkedClient && (
        <ConfirmModal
          title={`Remove client ${linkedClient.email}?`}
          message="This client will lose access to this portfolio. You can re-invite them later."
          confirmLabel="Remove Client"
          onConfirm={() => {
            unlinkClient(portfolioId);
            setLinkedClient(null);
            setShowUnlinkClient(false);
            toast.success('Client removed from portfolio');
          }}
          onCancel={() => setShowUnlinkClient(false)}
        />
      )}

      {/* Invite Client Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite Client</h3>
            <p className="text-sm text-gray-500 mb-4">
              Send a personalized invite link for <span className="font-medium text-gray-700">{name}</span> to a client.
              The client will get view-only access to this portfolio.
            </p>
            <input
              type="email"
              placeholder="client@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
            />
            {inviteUrl && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-green-700 mb-1 font-medium">Invite link created (copied to clipboard):</p>
                <p className="text-xs text-green-800 font-mono break-all select-all">{inviteUrl}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="btn-secondary text-sm" onClick={() => setShowInvite(false)}>
                {inviteUrl ? 'Done' : 'Cancel'}
              </button>
              {inviteUrl && (
                <button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-1.5"
                  onClick={async () => {
                    const result = await sendInviteEmail({
                      to: inviteEmail.trim(),
                      advisorEmail: user.email,
                      portfolioName: name,
                      inviteUrl,
                    });
                    if (result.sent) {
                      toast.success('Invite email sent successfully!');
                    } else {
                      toast.success('Opening your email client to send the invite...');
                    }
                  }}
                >
                  Send Email
                </button>
              )}
              {!inviteUrl && (
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim()}
                >
                  Generate Invite
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ticker summary modal */}
      {summaryTicker && (
        <TickerSummaryModal ticker={summaryTicker} onClose={() => setSummaryTicker(null)} />
      )}
    </div>
  );
}
